import { mulBp, withinRange } from '../math/fixed.ts';
import type { BattleState, CraneRules, CraneState, SendaNpc } from '../types.ts';
import { damageHero, ev } from './combat.ts';
import { gargFlock, gargTick } from './garg.ts';
import { shootTick } from './workshop.ts';

/**
 * The crane (docs/sendas/artifice.md, chapter 2, act 1, «La grúa»; the ground in battle/cable-layout.ts). The hanging
 * quarry on the far face of the abyss; Tormo's arms hang from a dwarven crane on its edge. Dunna works the crane's winch
 * and lowers the three pieces to the car below, `lower` each, while gargoyles come in flocks off the abyss: some go
 * for her or the hero, the rest sit on the crane's arm (battle/garg.ts). With `tilt` of weight on it (one past `heavyX`, at the arm's long end,
 * weighs double) the arm leans, the
 * piece on the hook sways (it shows) and Dunna stops the winch till they are struck off; swaying `fall` on end, the
 * piece comes off the hook and drops into the abyss: lost. Swaying, the piece sweeps the scaffold's tip (`swing`,
 * warned 3 s first): the arm's far perches are reached from there, so they are best struck off before it sways. Lost
 * with Dunna or the hero down; won with the three pieces in the car.
 *
 * Cost: the flyers' (battle/garg.ts); the crane is a handful of numbers.
 */

/** The weight on the arm: each gargoyle perched on it, double past `heavyX` (the lever's long end). */
export const craneWeight = (s: BattleState): number => {
  const r = s.rules?.senda?.crane;
  const gr = s.rules?.senda?.garg;
  let n = 0;
  for (const g of s.senda?.garg?.list ?? []) {
    if (g.mode !== 1 || !r || !gr) continue;
    const p = gr.perches[g.perch];
    if (p && p.kind === 0) n += p.x >= r.heavyX ? 2 : 1;
  }
  return n;
};

const fresh = (r: CraneRules): CraneState => ({ done: 0, prog: 0, sway: -1, swing: -1, flock: r.flock.first, shot: 0, end: -1, dropped: false });

export const craneTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.crane;
  const st = s.senda;
  if (!r || !st) return;
  const w = (st.crane ??= fresh(r));
  if (!s.started) return;
  gargTick(s);
  const d = st.npcs.find((n): n is SendaNpc => n.id === 'dunna' && n.hp > 0);
  if (w.end >= 0 || w.dropped) return;
  // A flock off the abyss.
  if (s.waveT >= w.flock) {
    w.flock = s.waveT + r.flock.every;
    gargFlock(s, r.flock.n, 0, [0], r.flock.perchBp);
    cue('flock');
  }
  // The arm leans with enough on it: the piece sways, Dunna stops; long enough, it drops.
  const on = craneWeight(s);
  if (on >= r.tilt) {
    if (w.sway < 0) {
      w.sway = 0;
      w.swing = s.tick + r.swing.warn;
      ev(s, { t: 'senda', kind: 'armTilt', npc: '', x: r.hook.x, y: r.hook.y, n: 1 });
      ev(s, { t: 'senda', kind: 'swingWarn', npc: '', x: r.swing.at.x, y: r.swing.at.y, n: r.swing.warn });
      cue('tilt');
    }
    w.sway++;
    if (w.sway * 2 >= r.fall) cue('sway');
    if (w.sway >= r.fall) {
      w.dropped = true;
      ev(s, { t: 'senda', kind: 'pieceDrop', npc: '', x: r.hook.x, y: r.hook.y, n: w.done });
      return;
    }
    if (s.tick >= w.swing) {
      w.swing = s.tick + r.swing.every;
      ev(s, { t: 'senda', kind: 'swing', npc: '', x: r.swing.at.x, y: r.swing.at.y });
      for (const h of s.heroes) if (h.downTicks === 0 && withinRange(h.x, h.y, r.swing.at.x, r.swing.at.y, r.swing.r)) damageHero(s, h, mulBp(h.stats.maxHp, r.swing.heroBp), null, 'physical', true);
    }
  } else if (w.sway >= 0) {
    w.sway = -1;
    w.swing = -1;
    ev(s, { t: 'senda', kind: 'armTilt', npc: '', x: r.hook.x, y: r.hook.y, n: 0 });
    cue('steady');
  }
  // Dunna at the winch, the arm steady: the piece goes down (a blow stops her a moment).
  if (d) {
    if (d.flinch > 0) d.flinch--;
    else if (w.sway < 0) {
      if (w.prog === 0) cue(`lower${w.done + 1}`);
      if (++w.prog >= r.lower) {
        w.prog = 0;
        w.done++;
        ev(s, { t: 'senda', kind: 'pieceDown', npc: '', x: r.hook.x, y: r.hook.y, n: w.done });
        cue(`piece${w.done}`);
        if (w.done >= r.pieces) w.end = s.tick;
      }
    }
    w.shot = shootTick(s, d, r.dunna, w.shot);
  }
};

/** The pieces' way down: ticks lowered of all three. */
export const craneGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.crane;
  const w = s.senda?.crane;
  if (!r || !w) return { done: 0, need: 1 };
  return { done: w.done * r.lower + w.prog, need: r.pieces * r.lower };
};

/** Won with the three pieces in the car (a moment after); lost with a piece dropped. */
export const craneResult = (s: BattleState): 'won' | 'lost' | null => {
  const w = s.senda?.crane;
  if (!w) return null;
  if (w.dropped) return 'lost';
  return w.end >= 0 && s.tick >= w.end + 24 ? 'won' : null;
};
